<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu 
      :user="user" 
      :isMenuOpen="isMenuOpen" 
      @toggle-menu="toggleMenu" 
      @close-menu="closeMenu" 
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <div class="flex items-center justify-between mb-6">
            <h1 class="text-xl font-bold">Seguimientos de {{ prospectName }}</h1>
            <button 
              @click="goBack" 
              class="hidden inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              Volver
            </button>
          </div>
          
          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
          
          <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ errorMessage }}
          </div>

          <div v-else-if="noDataAvailable" class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            Sin información disponible
          </div>

          <div v-if="!loading && !errorMessage && !noDataAvailable">
            <div v-if="seguimientos.length === 0" class="bg-white shadow overflow-hidden rounded-md p-6 text-center">
              <p class="text-gray-500">No se encontraron seguimientos para este prospecto.</p>
            </div>
            
            <div v-else class="mb-6">
              <h2 class="text-lg font-semibold mb-2">Historial de Seguimientos:</h2>

              <!-- Vista móvil: tarjetas individuales -->
              <div class="grid grid-cols-1 gap-4 sm:hidden">
                <div v-for="(seguimiento, index) in seguimientos" :key="index" class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4 border-blue-500">
                  <div class="grid grid-cols-2 gap-y-2">
                    <div class="text-sm font-medium text-gray-500">Tipo de Contacto:</div>
                    <div class="text-sm">{{ seguimiento.ContactType }}</div>

                    <div class="text-sm font-medium text-gray-500">Fecha:</div>
                    <div class="text-sm">{{ formatDate(seguimiento.DateStamp) }}</div>

                    <div class="col-span-2 text-sm font-medium text-gray-500 mt-2">Comentarios:</div>
                    <div class="col-span-2 text-sm">{{ seguimiento.Comments }}</div>
                  </div>
                </div>
              </div>

              <!-- Vista escritorio: tabla tradicional -->
              <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg hidden sm:block">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Tipo de Contacto</th>
                      <th class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Fecha</th>
                      <th class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Comentarios</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 bg-white font-medium">
                    <tr v-for="(seguimiento, index) in seguimientos" :key="index">
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {{ seguimiento.ContactType }}
                      </td>
                      <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {{ formatDate(seguimiento.DateStamp) }}
                      </td>
                      <td class="px-3 py-4 text-sm text-gray-900">
                        {{ seguimiento.Comments }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Botón para agregar nuevo seguimiento -->
            <div class="flex justify-end mt-6">
              <button 
                @click="addFollowUp()" 
                class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Agregar seguimiento
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import { getProspectsForFollowUp, getProspectActivities } from '@/services/api.js';

export default {
  components: {
    SidebarMenu
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      loading: false,
      errorMessage: null,
      noDataAvailable: false,
      prospectId: null,
      memberId: null,
      prospectName: 'Prospecto',
      seguimientos: []
    };
  },
  async created() {
    // Capturar el ID del prospecto de la ruta
    this.prospectId = parseInt(this.$route.params.prospectId);
    
    await this.getUserFromBackend();
    if (this.user) {
      await this.loadProspectBasicInfo();
      await this.loadSeguimientos();
    }
  },
  methods: {
    formatDateTime(dateString) {
      if (!dateString) return 'No especificada';
      try {
        return new Date(dateString).toLocaleString('es-MX', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'No especificada';
      try {
        return new Date(dateString).toLocaleString('es-MX', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      } catch (error) {
        return dateString;
      }
    },
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },
    async loadProspectBasicInfo() {
      try {
        this.loading = true;
        this.errorMessage = null;
        this.noDataAvailable = false;
        
        let userName = '';
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          userName = this.user.email.match(/^a(\d+)@/)[1];
        }
        
        // Obtener información básica del prospecto usando la función del api.js
        const response = await getProspectsForFollowUp(userName);
        
        // Filtrar manualmente por ProspectId
        if (response.data && Array.isArray(response.data.prospects)) {
          const foundProspect = response.data.prospects.find(p => 
            parseInt(p.ProspectId) === this.prospectId
          );
          
          if (foundProspect) {
            this.prospectName = foundProspect.ProspectName || 'Prospecto';
            this.memberId = foundProspect.MemberId || null;
          } else {
            this.noDataAvailable = true;
          }
        } else {
          this.noDataAvailable = true;
        }
      } catch (error) {
        console.error('Error al cargar información básica del prospecto:', error);
        
        // 404 = URL existe pero no hay datos para este prospecto (azul)
        if (error.response && error.response.status === 404) {
          this.noDataAvailable = true;
          return;
        }
        
        // Cualquier otro error = problema de conexión/URL/servidor (rojo)
        this.errorMessage = 'Error al cargar información del prospecto. Por favor, intente nuevamente.';
      }
    },
    async loadSeguimientos() {
      try {
        if (!this.prospectId || !this.memberId) {
          this.noDataAvailable = true;
          this.loading = false;
          return;
        }
        
        this.errorMessage = null;
        this.noDataAvailable = false;
        
        // Obtener seguimientos usando la función del api.js
        const response = await getProspectActivities(this.prospectId, this.memberId);
        
        if (response.data && Array.isArray(response.data.prospectsActivities)) {
          if (response.data.prospectsActivities.length > 0) {
            this.seguimientos = response.data.prospectsActivities;
          } else {
            // La API respondió correctamente pero no hay seguimientos
            this.seguimientos = [];
            this.noDataAvailable = true;
          }
        } else {
          this.noDataAvailable = true;
        }
      } catch (error) {
        console.error('Error al cargar seguimientos:', error);
        
        // 404 = URL existe pero no hay datos para este prospecto (azul)
        if (error.response && error.response.status === 404) {
          this.noDataAvailable = true;
          this.seguimientos = [];
          return;
        }
        
        // Cualquier otro error = problema de conexión/URL/servidor (rojo)
        this.errorMessage = 'Error al cargar los seguimientos. Por favor, intente nuevamente.';
      } finally {
        this.loading = false;
      }
    },
    addFollowUp() {
      this.$router.push(`/agregar-seguimiento/${this.prospectId}`);
    },
    goBack() {
      this.$router.go(-1); // Volver a la página anterior
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    async logout() {
      try {
        // Obtener nuevo token CSRF
        await axios.get('/sanctum/csrf-cookie');
        
        // Esperar un momento para asegurar que la cookie se haya establecido
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Realizar la petición de logout
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        // Limpiar cookies locales
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });

        // Redirigir al login
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>